import { useEffect, useRef, useState } from 'react';
import { ShieldQuestion, Clock, CheckCircle2, XCircle } from 'lucide-react';
import {
  joinAsGuest,
  fetchGuestSession,
  storedGuestToken,
  clearGuestToken,
  type GuestSession,
} from './guest-session';

/** How a guest (re)creates its session — mission (`joinAsGuest`) or invite path. */
type JoinFn = (code: string, name: string) => Promise<GuestSession | null>;
import { GuestMap } from './guest-map';
import { GuestAudio } from './guest-audio';
import vtakLogo from '@/assets/vtak-logo.png';

/** Poll cadence for the guest's approval/expiry state. */
const POLL_MS = 8_000;

type Phase = 'joining' | 'pending' | 'approved' | 'ended';

/** Human copy per phase. */
const PHASE_COPY: Record<Phase, { title: string; body: string }> = {
  joining: { title: 'Requesting access…', body: 'Starting a guest session for this mission.' },
  pending: {
    title: 'Waiting for approval',
    body: 'The mission leader has been notified. This device stays a guest until approved — no login needed.',
  },
  approved: {
    title: "You're in — view only",
    body: 'A leader approved your guest access. It lasts until it expires or a leader removes you.',
  },
  ended: {
    title: 'Guest session ended',
    body: 'Your access expired or was removed. Scan the mission QR again to request a new session.',
  },
};

/** Icon per phase. */
function PhaseIcon({ phase }: { phase: Phase }): React.JSX.Element {
  if (phase === 'approved') return <CheckCircle2 className="h-7 w-7 text-green-400" />;
  if (phase === 'ended') return <XCircle className="h-7 w-7 text-red-400" />;
  if (phase === 'pending') return <Clock className="h-7 w-7 animate-pulse text-echo-yellow" />;
  return <ShieldQuestion className="h-7 w-7 text-muted-foreground" />;
}

/**
 * Full-screen guest landing for a scanned mission QR. Joins as an anonymous
 * guest (no login), persists the token, and polls approval/expiry — showing the
 * appropriate state until a leader approves (view-only) or the session ends.
 */
export function GuestLanding({ joinCode, join }: { joinCode: string; join?: JoinFn }): React.JSX.Element {
  const [session, setSession] = useState<GuestSession | null>(null);
  const [phase, setPhase] = useState<Phase>('joining');
  const started = useRef(false);
  const joinFn = join ?? joinAsGuest;

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    function apply(next: GuestSession | null): void {
      setSession(next);
      setPhase(next ? (next.status === 'approved' ? 'approved' : 'pending') : 'ended');
    }

    async function bootstrap(): Promise<void> {
      const existing = storedGuestToken(joinCode);
      if (existing) {
        const found = await fetchGuestSession(existing);
        if (!found) clearGuestToken(joinCode);
        apply(found ?? (await joinFn(joinCode, 'Guest')));
      } else {
        apply(await joinFn(joinCode, 'Guest'));
      }
    }

    void bootstrap();
  }, [joinCode, joinFn]);

  useEffect(() => {
    const id = setInterval(async () => {
      const token = storedGuestToken(joinCode);
      if (!token) return;
      const next = await fetchGuestSession(token);
      if (!next) {
        clearGuestToken(joinCode);
        setSession(null);
        setPhase('ended');
        return;
      }
      setSession(next);
      setPhase(next.status === 'approved' ? 'approved' : 'pending');
    }, POLL_MS);
    return () => clearInterval(id);
  }, [joinCode]);

  // Approved + view-granted: hand the guest the read-only mission-area map with
  // a compact status/listen overlay instead of the full-screen status card.
  if (phase === 'approved' && session?.viewGranted) {
    return <GuestMapView joinCode={joinCode} session={session} />;
  }

  const copy = PHASE_COPY[phase];
  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-[#0d0d0d] p-4">
      <div className="w-full max-w-sm rounded-xl border border-echo-border bg-[#1a1a1a] p-6 text-center shadow-2xl">
        <img src={vtakLogo} alt="VTAK" className="mx-auto mb-4 h-11 w-auto" />
        <div className="mb-3 flex justify-center">
          <PhaseIcon phase={phase} />
        </div>
        <h1 className="text-base font-semibold text-foreground">{copy.title}</h1>
        <p className="mx-auto mt-2 max-w-xs text-xs leading-relaxed text-muted-foreground">{copy.body}</p>
        <p className="mt-4 font-mono text-[11px] tracking-widest text-muted-foreground/70">
          MISSION {joinCode}
        </p>
        {session?.expiresAt && phase !== 'ended' && (
          <p className="mt-1 text-[10px] text-muted-foreground/60">
            Guest access expires {new Date(session.expiresAt).toLocaleString()}
          </p>
        )}
        {phase === 'approved' && (
          <p className="mt-3 text-[11px] text-muted-foreground">
            View-only access granted. The leader hasn’t shared the mission area map.
          </p>
        )}
        {phase === 'approved' && session?.audioGranted && <GuestAudio token={session.guestToken} />}
      </div>
    </div>
  );
}

/** Full-screen read-only mission-area map with a compact guest overlay. */
function GuestMapView({ joinCode, session }: { joinCode: string; session: GuestSession }): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-[2000] bg-[#0d0d0d]">
      <GuestMap token={session.guestToken} />
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center p-3">
        <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-echo-border bg-[#1a1a1a]/90 px-4 py-1.5 shadow-lg backdrop-blur">
          <img src={vtakLogo} alt="VTAK" className="h-5 w-auto" />
          <span className="text-[11px] font-semibold text-foreground">Mission area — view only</span>
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground/70">{joinCode}</span>
        </div>
      </div>
      {session.audioGranted && (
        <div className="absolute bottom-3 right-3 w-60 rounded-xl border border-echo-border bg-[#1a1a1a]/90 p-2 shadow-lg backdrop-blur">
          <GuestAudio token={session.guestToken} />
        </div>
      )}
    </div>
  );
}
